import React, { FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { WhitePaperFormContext } from 'buyside/components/presentation/WhitePaperForm/whitePaperForm';
import AttachmentPreview from 'buyside/components/presentation/WhitePaperForm/WriteupAndAttachments/Attachments/attachmentPreview';
import Attachments from 'buyside/components/presentation/WhitePaperForm/WriteupAndAttachments/Attachments/attachments';
import Writeup from 'buyside/components/presentation/WhitePaperForm/WriteupAndAttachments/Writeup/writeup';

import { WhitePaperFormInput } from 'buyside/util/researchSubmission';

import FileTextSVG from 'buyside/svgs/file_text.svg';
import PaperStackSVG from 'buyside/svgs/paper_stack.svg';
import PenPaperSVG from 'buyside/svgs/pen_and_paper.svg';

export type AttachmentGroups = {
  [researchType: string]: {
    description: string;
    icon: string;
    attachmentPreviewIndices: { [index: number]: ReactNode };
  };
};

const WriteupAndAttachments: FC = () => {
  const { whitePaperId } = useContext(WhitePaperFormContext) || {};

  if (!whitePaperId) throw new Error();

  const {
    formState: { defaultValues },
    getValues
  } = useFormContext<WhitePaperFormInput>();

  const { append, fields, remove, update } = useFieldArray<WhitePaperFormInput>({
    name: 'attachments'
  });

  const attachmentGroups = useMemo(
    () =>
      fields.reduce<AttachmentGroups>(
        (acc, field, index) => {
          const researchType = getValues(`attachments.${index}.researchType`);

          acc[researchType] = {
            ...acc[researchType],
            attachmentPreviewIndices: {
              ...acc[researchType].attachmentPreviewIndices,
              [index]: (
                <AttachmentPreview key={field.id} index={index} remove={remove} update={update} />
              )
            }
          };

          return acc;
        },
        {
          thesis: {
            description: 'Attach a PDF to use as your thesis.',
            icon: PenPaperSVG,
            attachmentPreviewIndices: {}
          },
          financial_model: {
            description: 'Upload a file that supplements the information laid out in your thesis.',
            icon: FileTextSVG,
            attachmentPreviewIndices: {}
          },
          secondary: {
            description: 'Analyst reports, media articles, legal filings, etc.',
            icon: PaperStackSVG,
            attachmentPreviewIndices: {}
          }
        }
      ),
    [fields, getValues, remove, update]
  );

  const { thesis: thesisAttachmentGroup, ...remainingAttachmentGroups } = attachmentGroups;

  const [thesisEditorHidden, setThesisEditorHidden] = useState(
    Boolean(Object.keys(thesisAttachmentGroup.attachmentPreviewIndices).length) ||
      !defaultValues?.thesis
  );

  const handleRemoveThesisAttachment = useCallback(() => {
    const thesisAttachmentIndex = Object.keys(thesisAttachmentGroup.attachmentPreviewIndices)[0];
    if (thesisAttachmentIndex) remove(Number(thesisAttachmentIndex));
  }, [remove, thesisAttachmentGroup]);

  return (
    <>
      <Writeup
        append={append}
        handleRemoveThesisAttachment={handleRemoveThesisAttachment}
        setThesisEditorHidden={setThesisEditorHidden}
        thesisAttachmentGroup={thesisAttachmentGroup}
        thesisEditorHidden={thesisEditorHidden}
      />
      <Attachments append={append} attachmentGroups={remainingAttachmentGroups} />
    </>
  );
};

export default WriteupAndAttachments;
